import { Image, StyleSheet, Text, View } from "@react-pdf/renderer";

export const RECEITA_STRIPE: Record<
  "A" | "B" | "B2" | "NRR" | "NRT" | "RCE",
  string
> = {
  A: "#E8C84A",
  B: "#A2C1D4",
  B2: "#A2C1D4",
  NRR: "#D9D2C5",
  NRT: "#D9D2C5",
  RCE: "#E8E8E8",
};

export const notificationStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    paddingTop: 28,
    paddingRight: 28,
    paddingBottom: 24,
    paddingLeft: 48,
    fontSize: 9,
    position: "relative",
  },
  stripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 28,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
  },
  titleBlock: {
    flex: 1,
    alignItems: "center",
    paddingRight: 8,
  },
  titleMain: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    textAlign: "center",
    letterSpacing: 0.3,
  },
  titleLetter: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
  },
  eletronica: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 2,
    marginTop: 2,
    textAlign: "center",
  },
  numeroWrap: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#000",
    paddingVertical: 4,
    paddingHorizontal: 6,
    minWidth: 130,
  },
  numeroLabel: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginRight: 6,
  },
  numeroValue: {
    fontSize: 10,
    fontFamily: "Helvetica",
    flex: 1,
    minHeight: 12,
  },
  twoCol: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 10,
  },
  col: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#000",
    minHeight: 108,
  },
  colHeader: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 4,
    backgroundColor: "#F3F3F3",
  },
  colTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    textTransform: "uppercase",
  },
  colBody: {
    padding: 8,
  },
  field: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  value: {
    fontFamily: "Helvetica",
    textTransform: "none",
  },
  prescriptionBox: {
    borderWidth: 1,
    borderColor: "#000",
    minHeight: 260,
    marginBottom: 12,
    flexGrow: 1,
  },
  prescriptionHeader: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 4,
    backgroundColor: "#F3F3F3",
  },
  prescriptionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    textTransform: "uppercase",
  },
  prescriptionBody: {
    padding: 10,
  },
  fieldSpaced: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    marginBottom: 16,
    textTransform: "uppercase",
  },
  footerDigital: {
    marginTop: "auto",
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerTextCol: {
    flex: 1,
    paddingRight: 12,
  },
  footerLine: {
    fontSize: 7,
    fontFamily: "Helvetica-Oblique",
    marginBottom: 2,
    color: "#333333",
    textTransform: "uppercase",
  },
  footerBold: {
    fontFamily: "Helvetica-BoldOblique",
  },
  qrImage: {
    width: 64,
    height: 64,
  },
  qrPair: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-end",
  },
  qrItem: {
    alignItems: "center",
    width: 70,
  },
  qrCaption: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    marginTop: 2,
    textAlign: "center",
    textTransform: "uppercase",
  },
  warningBox: {
    borderWidth: 1,
    borderColor: "#000",
    padding: 6,
    backgroundColor: "#F5F5F5",
    marginBottom: 8,
  },
  warningText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 2,
  },
});

export type NotificationPdfProps = {
  filterSpecialMedication: any[];
  dataPdf: any;
  gender?: "male" | "female";
  linkDocument?: string;
  qrCodeDataUrl?: string | null;
  qrCodeSncrDataUrl?: string | null;
  sncrConsultaUrl?: string;
};

export function NotificationStripe({ color }: { color: string }) {
  return (
    <View style={[notificationStyles.stripe, { backgroundColor: color }]} />
  );
}

export function NotificationDigitalFooter({
  linkDocument,
  qrCodeSncrDataUrl,
  platformName = "Fluxmed",
}: {
  linkDocument?: string;
  /** Mantido na API dos callers; QR Fluxmed (esquerda) removido do rodapé. */
  qrCodeDataUrl?: string | null;
  qrCodeSncrDataUrl?: string | null;
  sncrConsultaUrl?: string;
  platformName?: string;
}) {
  const s = notificationStyles;
  return (
    <View style={s.footerDigital} fixed>
      <View style={s.footerTextCol}>
        <Text style={s.footerLine}>
          Identificação da plataforma: {platformName}
        </Text>
        <Text style={s.footerLine}>
          Registro da assinatura digital e data
        </Text>
        <Text style={s.footerLine}>
          Valide essa assinatura em{" "}
          <Text style={s.footerBold}>validar.iti.gov.br</Text>
        </Text>
        <Text style={s.footerLine}>
          Esta prescrição pode ser verificada em:
          {linkDocument ? ` ${linkDocument}` : ""}
        </Text>
        <Text style={s.footerLine}>
          Verifique a numeração e registre a utilização desta prescrição em{" "}
          <Text style={s.footerBold}>sncr.anvisa.gov.br</Text>
        </Text>
      </View>
      <View style={s.qrPair}>
        <View style={s.qrItem}>
          {qrCodeSncrDataUrl ? (
            <Image src={qrCodeSncrDataUrl} style={s.qrImage} />
          ) : (
            <View style={[s.qrImage, { borderWidth: 1, borderColor: "#999" }]} />
          )}
          <Text style={s.qrCaption}>SNCR</Text>
        </View>
      </View>
    </View>
  );
}

export function getPrescritorNome(dataPdf: any): string {
  return (
    dataPdf?.[0]?.professionalData?.practitioner?.display ||
    dataPdf?.[0]?.professionalData?.practitioner?.name?.[0]?.text ||
    ""
  );
}

export function getCrmParts(dataPdf: any): { crm: string; uf: string } {
  const raw =
    dataPdf?.[0]?.professionalData?.identifier?.[0]?.value?.split("/")?.[1] ||
    "";
  return {
    crm: raw.slice(2) || "",
    uf: raw.slice(0, 2) || "",
  };
}

export function getClinicAddress(dataPdf: any): string {
  const auxAddress = dataPdf?.[0]?.organization?.contact?.find(
    (el: any) => el.address,
  )?.address;
  const city = auxAddress?.city || "";
  const state = auxAddress?.state || "";
  const postalCode = auxAddress?.postalCode || "";
  const line = auxAddress?.line?.join(", ") || "";
  const mainAddress =
    [line, city, state, postalCode]?.filter((el: any) => el)?.join(", ") || "";

  const locLine = dataPdf?.[0]?.location?.address?.line;
  if (Array.isArray(locLine) && locLine.length) {
    return [...locLine].reverse().join(", ");
  }
  return mainAddress || "-";
}

export function getPatientName(dataPdf: any): string {
  return dataPdf?.[0]?.patientData || "";
}

export function getPatientCpf(dataPdf: any): string {
  return dataPdf?.[0]?.patientCPF || "";
}

export function getMedFields(item: any) {
  return {
    nome: item?.label || item?.name || "",
    concentracao: item?.inputValue?.presentation || "",
    forma: item?.formValue?.label || "",
    quantidade: item?.quantity != null ? String(item.quantity) : "",
    posologia: item?.form || item?.note || "",
    note: item?.note || "",
    sncrNumero: item?.sncrNumero || "",
  };
}
